// Receiving workbench (PX-PUR-220) — supabase/migrations/20261027200000.
//
//   queue   rpc receiving_queue(company, term, days) — receivable orders due
//           within the window, or matching a scan (PO number prefix,
//           supplier reference, supplier, exact item code); ≤ 100 + has_more
//   today   rpc receiving_today(company) — posted receipts of the company day
//           with their first 20 lines, lots and the inspections they created; ≤ 30
//
// Posting stays post_purchase_receipt (ReceiveForm on the order document).

import { queryOptions } from "@tanstack/react-query";
import { isDemoMode, supabase } from "@/lib/supabase";
import type { PurchaseOrderStatus } from "./api";

export type ReceivingMatch = "number" | "reference" | "supplier" | "item";

export interface ReceivingOrder {
  id: string;
  number: string;
  status: PurchaseOrderStatus;
  supplier_id: string;
  supplier_code: string;
  supplier_name: string;
  supplier_reference: string | null;
  next_promised_date: string | null;
  warehouse_code: string | null;
  line_count: number;
  open_lines: number;
  received_lines: number;
  match: ReceivingMatch | null;
}

export interface ReceivingQueue {
  today: string;
  has_more: boolean;
  /** More than 50 suppliers matched the term: supplier search was skipped. */
  suppliers_too_broad?: boolean;
  orders: ReceivingOrder[];
}

export interface TodayReceipt {
  id: string;
  number: string;
  posted_at: string;
  order_id: string;
  order_number: string;
  supplier_name: string;
  delivery_note: string | null;
  posted_by_name: string | null;
  is_reversal: boolean;
  reversed: boolean;
  /** All lines of the receipt; `lines` holds the first 20. */
  line_count: number;
  lines: {
    line_no: number;
    item_code: string;
    item_description: string;
    qty: number | string;
    uom_code: string;
    location_code: string | null;
    lot_id: string | null;
    lot_code: string | null;
    serial_count: number;
  }[];
  inspections: { id: string; number: string; status: "open" | "in_progress" | "decided" | "cancelled" }[];
}

export interface ReceivingToday {
  since: string;
  has_more: boolean;
  receipts: TodayReceipt[];
}

/** Due windows offered on the screen (days ahead; overdue is always in). */
export const receivingWindows = [0, 3, 7, 14, 30] as const;
export type ReceivingWindow = (typeof receivingWindows)[number];
export const isReceivingWindow = (v: unknown): v is ReceivingWindow =>
  typeof v === "number" && (receivingWindows as readonly number[]).includes(v);

export const receivingKeys = {
  all: ["procurement", "receiving"] as const,
  queue: (companyId: string, term: string, days: number) => [...receivingKeys.all, "queue", companyId, term, days] as const,
  today: (companyId: string) => [...receivingKeys.all, "today", companyId] as const,
};

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

const emptyQueue: ReceivingQueue = { today: "", has_more: false, orders: [] };

export function receivingQueueQuery(companyId: string, term: string, days: number) {
  return queryOptions({
    queryKey: receivingKeys.queue(companyId, term, days),
    queryFn: async ({ signal }): Promise<ReceivingQueue> => {
      if (isDemoMode) return emptyQueue;
      const { data, error } = await db()
        .rpc("receiving_queue", { p_company_id: companyId, p_term: term || null, p_days: days })
        .abortSignal(signal);
      if (error) throw error;
      return (data ?? emptyQueue) as ReceivingQueue;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    placeholderData: (prev) => prev,
    enabled: !!companyId,
  });
}

export function receivingTodayQuery(companyId: string) {
  return queryOptions({
    queryKey: receivingKeys.today(companyId),
    queryFn: async ({ signal }): Promise<ReceivingToday> => {
      if (isDemoMode) return { since: "", has_more: false, receipts: [] };
      const { data, error } = await db().rpc("receiving_today", { p_company_id: companyId }).abortSignal(signal);
      if (error) throw error;
      return (data ?? { since: "", has_more: false, receipts: [] }) as ReceivingToday;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    enabled: !!companyId,
  });
}

/** Whole days the date is before today (positive = overdue), from ISO dates. */
export function daysLate(date: string | null, today: string): number {
  if (!date || !today) return 0;
  return Math.round((Date.parse(`${today}T12:00:00Z`) - Date.parse(`${date}T12:00:00Z`)) / 86_400_000);
}
